import { IResponse } from '@/types/common';
import {
  IImportKpiAppUsageRequest,
  IKpiAppUsagePeriod,
  ISaveKpiInitiativeRequest,
} from '@/types/request';
import {
  ICompanyReport,
  ICompanyReportCompanyOption,
  ICompanyReportFamilyOption,
  IKpiAppUsage,
  IKpiFamilyCohort,
  IKpiInitiative,
  IKpiLatestResponse,
  IKpiSnapshot,
} from '@/types/response';
import axiosClient from '../axiosClient';
import { ENDPOINT } from '../endpoint';

class Kpi {
  /** 期間内の日次スナップショット（古い順）。from/to 省略時はサーバーが最新日から90日分を返す */
  list = (params: { from?: string; to?: string }): Promise<IResponse<IKpiSnapshot[]>> =>
    axiosClient.get(ENDPOINT.KPI.LIST, { params });

  /** 最新の行（団体コード別の内訳込み）と、前日・7日前の行 */
  latest = (): Promise<IResponse<IKpiLatestResponse>> =>
    axiosClient.get(ENDPOINT.KPI.LATEST);

  /** 登録週ごとの家族化（新しい週から順）。weeks 省略時は12週 */
  familyCohorts = (params: { weeks?: number }): Promise<IResponse<IKpiFamilyCohort[]>> =>
    axiosClient.get(ENDPOINT.KPI.FAMILY_COHORTS, { params });
}

export const KpiService = new Kpi();

class KpiInitiative {
  /** 全件（日付の古い順） */
  getList = (): Promise<IResponse<IKpiInitiative[]>> =>
    axiosClient.get(ENDPOINT.KPI_INITIATIVE.GET_LIST);

  create = (body: ISaveKpiInitiativeRequest): Promise<IResponse<IKpiInitiative>> =>
    axiosClient.post(ENDPOINT.KPI_INITIATIVE.CREATE, body);

  update = (id: number, body: ISaveKpiInitiativeRequest): Promise<IResponse<IKpiInitiative>> =>
    axiosClient.put(ENDPOINT.KPI_INITIATIVE.UPDATE.replace(':id', String(id)), body);

  delete = (id: number) =>
    axiosClient.delete(ENDPOINT.KPI_INITIATIVE.DELETE.replace(':id', String(id)));
}

export const KpiInitiativeService = new KpiInitiative();

class KpiAppUsage {
  /** 取り込んだ全期間・全件（期間の古い順） */
  getList = (): Promise<IResponse<IKpiAppUsage[]>> =>
    axiosClient.get(ENDPOINT.KPI_APP_USAGE.GET_LIST);

  /** 1期間分を取り込む（同じ取得元・期間は置き換え） */
  import = (body: IImportKpiAppUsageRequest): Promise<IResponse<{ imported: number }>> =>
    axiosClient.put(ENDPOINT.KPI_APP_USAGE.IMPORT, body);

  deletePeriod = (params: IKpiAppUsagePeriod): Promise<IResponse<{ deleted: number }>> =>
    axiosClient.delete(ENDPOINT.KPI_APP_USAGE.DELETE, { params });
}

export const KpiAppUsageService = new KpiAppUsage();

class CompanyReport {
  /** 企業・家族と期間を指定したレポート。ID はカンマ区切りで送る */
  get = (params: {
    companyIds: number[];
    familyIds: number[];
    from: string;
    to: string;
  }): Promise<IResponse<ICompanyReport>> =>
    axiosClient.get(ENDPOINT.COMPANY_REPORT.GET, {
      params: {
        companyIds: params.companyIds.length ? params.companyIds.join(',') : undefined,
        familyIds: params.familyIds.length ? params.familyIds.join(',') : undefined,
        from: params.from,
        to: params.to,
      },
    });

  /** 企業の選択肢（団体コードと今適用中の家族数つき） */
  companies = (): Promise<IResponse<ICompanyReportCompanyOption[]>> =>
    axiosClient.get(ENDPOINT.COMPANY_REPORT.COMPANIES);

  /** 家族の検索（名前の一部または家族 ID。最大 20 件） */
  families = (q: string): Promise<IResponse<ICompanyReportFamilyOption[]>> =>
    axiosClient.get(ENDPOINT.COMPANY_REPORT.FAMILIES, { params: { q } });
}

export const CompanyReportService = new CompanyReport();
